import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import { UpdateHomeLink } from "../../redux/actions/UpdateHomeLinkAction";

const stripImageBasePath = (imagePath) => {
  if (!imagePath) {
    return "";
  }

  const imagesIndex = imagePath.toLowerCase().indexOf("/images/");

  if (imagesIndex >= 0) {
    return imagePath.substring(imagesIndex + "/images/".length);
  }

  return imagePath;
};

const EditHomeLinkModal = ({ open, onClose, homeLinkData }) => {
  const dispatch = useDispatch();
  const status = useSelector((state) => state.updateHomeLink.status);
  const [form, setForm] = useState({
    idhomeLink: "",
    name: "",
    url: "",
    imagePath: "",
    countryId: "",
  });
  const [message, setMessage] = useState({ type: "", text: "" });
  const loading = status === "loading";

  useEffect(() => {
    if (homeLinkData && open) {
      setForm({
        idhomeLink: homeLinkData.idhomeLink ?? "",
        name: homeLinkData.name ?? "",
        url: homeLinkData.url ?? "",
        imagePath: stripImageBasePath(homeLinkData.imagePath),
        countryId: homeLinkData.countryId ?? "",
      });
      setMessage({ type: "", text: "" });
    }
  }, [homeLinkData, open]);

  const isFormValid = () =>
    form.idhomeLink !== "" &&
    form.name.trim() !== "" &&
    form.url.trim() !== "" &&
    form.imagePath.trim() !== "";

  const handleClose = () => {
    onClose();
  };

  const handleSave = async () => {
    setMessage({ type: "", text: "" });

    try {
      const result = await dispatch(UpdateHomeLink(form));

      if (result.success) {
        setMessage({ type: "success", text: result.message });
        setTimeout(() => {
          handleClose();
        }, 1500);
      } else {
        setMessage({ type: "error", text: result.message });
      }
    } catch {
      setMessage({
        type: "error",
        text: "There was an error updating the home link. Please try again.",
      });
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Home Link</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <TextField
            label="ID"
            value={form.idhomeLink}
            fullWidth
            disabled
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
            required
            disabled={loading}
          />
          <TextField
            label="URL"
            value={form.url}
            onChange={(e) => setForm({ ...form, url: e.target.value })}
            fullWidth
            required
            disabled={loading}
          />
          <TextField
            label="Image Path"
            value={form.imagePath}
            onChange={(e) => setForm({ ...form, imagePath: e.target.value })}
            fullWidth
            required
            disabled={loading}
            helperText="Relative path inside the Images folder"
          />

          {message.text && (
            <Alert
              severity={message.type}
              onClose={() => setMessage({ type: "", text: "" })}
            >
              {message.text}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!isFormValid() || loading}
          startIcon={
            loading ? <CircularProgress size={20} color="inherit" /> : null
          }
          sx={{
            backgroundColor: "#4b4bfa",
            "&:hover": { backgroundColor: "#3e3ed9" },
            color: "#fff",
          }}
        >
          {loading ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditHomeLinkModal;
